import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Popover,
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  TextField,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useState, useEffect } from "react";

import { UseHandleLeads } from "0.Mine/1.Hooks/CRM/Leads";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { useSession } from "0.Mine/2.Context/sessionContext";
import { LeadState } from "0.Mine/3.Interfaces/lead";
import { StatusOption } from "0.Mine/3.Interfaces/lead";
import { SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";

interface StatusDialogProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSave: (status: StatusOption | null) => void;
  currentStatus: StatusOption | null;
  initialOptions: StatusOption[];
}

const defaultColors = ["#4caf50", "#ff9800", "#f44336", "#2196f3", "#9c27b0", "#795548", "#607d8b"];

const StatusDialog: React.FC<StatusDialogProps> = ({
  anchorEl,
  open,
  onClose,
  onSave,
  currentStatus,
  initialOptions,
}) => {
  const { session } = useBusinessContext();
  const { session: UserSession } = useSession();
  const [selectedStatus, setSelectedStatus] = useState<StatusOption | null>(currentStatus);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>(initialOptions);
  const [newStatusLabel, setNewStatusLabel] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { createStatusOption, deleteStatusOption } = UseHandleLeads();

  if (!session) return <p>Nessuna sessione presente!</p>;

  // Helper function to check if a status is a mandatory status from the LeadState enum
  const isMandatoryStatus = (label: string): boolean => {
    return Object.values(LeadState).includes(label as LeadState);
  };

  const handleStatusClick = (status: StatusOption) => {
    setSelectedStatus(status);
  };

  const handleAddNewStatus = async () => {
    if (!newStatusLabel.trim() || !session?.selectedBusiness) return;

    const newStatus: StatusOption = {
      id: Date.now().toString(),
      label: newStatusLabel.trim(),
      color: defaultColors[statusOptions.length % defaultColors.length],
    };

    const updatedOptions = [...statusOptions, newStatus];
    setStatusOptions(updatedOptions);
    setSelectedStatus(newStatus);
    setNewStatusLabel("");
    setIsEditing(false);

    try {
      const statusId = (
        await createStatusOption(session.selectedBusiness.id, newStatus.label)
      ).toString();
      if (statusId) {
        setStatusOptions((prev) =>
          prev.map((status) => (status.id === newStatus.id ? { ...status, id: statusId } : status))
        );
      }
    } catch (err) {
      console.error("Failed to save status options:", err);
      setError("Failed to save new status");
    }
  };

  const handleRemoveStatus = async (statusToRemove: StatusOption, event: React.MouseEvent) => {
    event.stopPropagation();

    // Early return if it's a mandatory status using the enum
    if (isMandatoryStatus(statusToRemove.label)) {
      // Optionally show a message explaining why it can't be deleted
      setError("Cannot delete mandatory statuses");
      setTimeout(() => setError(null), 3000); // Clear error after 3 seconds
      return;
    }

    if (!session?.selectedBusiness) return;

    const updatedOptions = statusOptions.filter((status) => status.id !== statusToRemove.id);
    setStatusOptions(updatedOptions);

    if (selectedStatus?.id === statusToRemove.id) {
      setSelectedStatus(null);
    }

    try {
      await deleteStatusOption(session.selectedBusiness.id, statusToRemove.id);
    } catch (err) {
      console.error("Failed to save status options:", err);
      setError("Failed to remove status");
    }
  };

  const handleSave = () => {
    onSave(selectedStatus);
    enqueueSnackbar("Salvato con successo", {
      variant: "success",
    });
    onClose();
  };

  if (isLoading) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Paper sx={{ width: 300, p: 3, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={40} />
        </Paper>
      </Popover>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Paper sx={{ width: 300, maxHeight: 400, overflow: "auto" }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Seleziona Esito
          </Typography>

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <List dense>
            {statusOptions.map((status) => (
              <ListItem
                key={status.id}
                disablePadding
                secondaryAction={
                  isMandatoryStatus(status.label) ? (
                    <div />
                  ) : (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={(e) => handleRemoveStatus(status, e)}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <ListItemButton
                  selected={selectedStatus?.id === status.id}
                  onClick={() => handleStatusClick(status)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <FiberManualRecordIcon sx={{ color: status.color }} />
                  </ListItemIcon>
                  <ListItemText primary={status.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2, borderColor: "divider" }} />

          {UserSession.user.qlickup === "user" && !isEditing ? (
            <SafeArgonButton
              startIcon={<AddIcon />}
              onClick={() => setIsEditing(true)}
              fullWidth
              size="medium" // Changed from small to medium
              variant="contained" // Changed from outlined to contained
              color="success" // Added color success to make it stand out
              sx={{
                py: 1, // Added padding to make it taller
                mt: 1, // Added margin top
                fontWeight: "bold", // Make text bold
              }}
            >
              Aggiungi nuovo esito
            </SafeArgonButton>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1, // Added padding
                bgcolor: "background.paper", // Background color
              }}
            >
              <TextField
                size="small"
                placeholder="Nome del nuovo status"
                value={newStatusLabel}
                onChange={(e) => setNewStatusLabel(e.target.value)}
                fullWidth
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddNewStatus();
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddNewStatus}
                sx={{
                  minWidth: 35,
                  height: 35,
                  borderRadius: "999px",
                  backgroundImage:
                    'url("https://qlickup.it/wp-content/uploads/2025/04/SFONDO-1.jpg")',
                  backgroundSize: "cover!important",
                  padding: "6px",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  transition: "all 0.2s ease",
                  minHeight: "unset",
                  color: "#ffffff!important",
                  "&:hover": {
                    rotate: "90deg",
                    backgroundImage:
                      'url("https://qlickup.it/wp-content/uploads/2025/04/SFONDO-1.jpg")',
                  },
                }} // Make the add icon button more visible
              >
                <AddIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <SafeArgonButton size="small" onClick={onClose} sx={{ mr: 1 }}>
              Cancella
            </SafeArgonButton>
            <SafeArgonButton
              size="small"
              color="primary"
              onClick={handleSave}
              disabled={!selectedStatus}
            >
              Applica
            </SafeArgonButton>
          </Box>
        </Box>
      </Paper>
    </Popover>
  );
};

export default StatusDialog;
